import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';

import type { AccessGroup } from '@/api/auth';
import type { ApiError } from '@/api/http';
import {
  checkDuplicates,
  createPerson,
  createPersonDespiteDuplicates,
  openConsideration,
  type ConsiderationOpened,
  type DuplicateCheck,
  type NewPersonAttempt,
  type Person,
  type PossibleDuplicate,
} from '@/api/people';
import { queryKeyRoots, queryKeys } from '@/api/queryKeys';
import { rolesQuery } from '@/api/vacancies';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Field } from '@/components/ui/Field';
import { Select } from '@/components/ui/Select';
import { mayWriteVacancies } from '@/pages/Vacancies/vacancyAccess';
import { VACANCIES_PATH } from '@/pages/Vacancies/vacancyUrls';
import { type } from '@/theme/typography';

import { DuplicateWarning } from './DuplicateWarning';
import { personFieldsSchema } from './personFields';
import { PersonFields } from './PersonFields';

/**
 * How long the typing pauses before the duplicate pre-check is asked.
 *
 * Same reasoning as `SEARCH_DEBOUNCE_MS`, on a different route: every authenticated route is
 * throttled at 300/min per user (BUG-011), and a check per keystroke would spend that budget on the
 * one screen a recruiter types the most on. It is also roughly the pause between two words, so the
 * body that goes out is usually the one they meant.
 */
export const DUPLICATE_CHECK_DEBOUNCE_MS = 300;

/**
 * The form is the schema (`.claude/rules/frontend.md` → Forms), and the four fields a **person** is
 * live in `personFields.ts` — §6.5's *Edit* writes the same four (TASK-066), and describing them
 * twice is how two forms end up asking for the same person in two different ways.
 *
 * What this screen adds is the one field §6.4 did not say was required:
 *
 *  * `vacancy` **required**, and that is TASK-064's one addition to §6.4 (recorded in `DESIGN.md` in
 *    the same pass): §6.3 is a table of **considerations**, so a person created without one appears
 *    in no row of the screen the dialog was opened from. Not "invisible until refreshed" — invisible.
 */
const candidateSchema = personFieldsSchema.extend({
  /** The vacancy id as the `Select` carries it; `ConsiderationIn.role_id` is a number. */
  vacancy: z.string().min(1),
});

type CandidateForm = z.infer<typeof candidateSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** In the pre-check's cache key, as in every other key on this surface (`queryKeys`). */
  viewerId: number;
  /**
   * The viewer's groups — **only** to decide which sentence the empty vacancy catalogue gets and
   * whether it may lead anywhere (BUG-020, `EmptyCatalogueNotice` below). Nothing here gates a
   * request or a write: the screen already renders this dialog for a viewer who may add a candidate
   * (`index.tsx`), and the server refuses everything else on its own.
   */
  groups: AccessGroup[];
}

/**
 * The three match keys, settled after a pause.
 *
 * The three primitives are the dependencies on purpose: the object is rebuilt on every keystroke, so
 * as a dependency it would restart the timer on every render and could hold a check back for as long
 * as the parent keeps rendering.
 */
function useSettledKeys(keys: DuplicateCheck, delay: number): DuplicateCheck {
  const [settled, setSettled] = useState(keys);
  const { full_name, email, phone } = keys;

  useEffect(() => {
    const timer = setTimeout(() => setSettled({ full_name, email, phone }), delay);
    return () => clearTimeout(timer);
  }, [full_name, email, phone, delay]);

  return settled;
}

/**
 * `DESIGN.md` §6.4 — **the enforcement point of *one person, one card, forever* (feedback protection
 * #7, ADR 0002 + ADR 0014).**
 *
 * Three properties are the task, and each is a way the protection can be present and worthless:
 *
 * 1. **A match is an interaction, not a validation error.** `DuplicateWarning` renders it: the
 *    matched person, what they were already considered for, why the matcher thinks it is them, and
 *    the two actions §6.4 fixes — a second consideration on the existing card first, a separate
 *    person second. Red text under the name would satisfy "the form says so" and destroy the point,
 *    because ADR 0002's whole subject is that the same human at a second vacancy is **not** a
 *    duplicate person.
 * 2. **Both paths to a match land in the same interface.** `POST /people/check-duplicates/` warns
 *    before saving, and `POST /people/` itself answers **409** — it is the enforcement point, and the
 *    pre-check can lose a race with a colleague typing the same name. A recruiter who submits fast
 *    gets the considered interface, not a raw failure.
 * 3. **The override is never silent.** The first create carries no `acknowledge_possible_duplicates`
 *    at all (`createPerson`); only *"Create a separate person"* sends it
 *    (`createPersonDespiteDuplicates`). ADR 0014: *"A duplicate is never created silently."*
 *
 * ⚠️ **Nothing personal reaches a request URL from here.** The check is a POST **with a body** for
 * exactly that reason (`.claude/rules/security.md`, and `DuplicateCheckIn`'s own docstring): a name
 * or a phone number in a query string is written verbatim into the access log of every hop and
 * outlives the request. BUG-009 was a 500 that emailed a candidate's name out of a query string.
 *
 * The dialog keeps its state in the component, not in the URL — §6.4 is *"dialog over
 * `/candidates`"*, and a half-typed candidate is not an address anyone should be able to share.
 */
export function AddCandidateDialog({ open, onOpenChange, viewerId, groups }: Props) {
  const { t } = useTranslation(['candidates', 'common', 'components']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<CandidateForm>({
    resolver: zodResolver(candidateSchema),
    defaultValues: { full_name: '', email: '', phone: '', source: '', vacancy: '' },
  });
  const values = form.watch();

  /**
   * The person `POST /people/` created while the run after it has **not** been opened — §6.4's
   * partial failure, and the reason it is state rather than a variable: it is what stops the retry
   * creating a second person.
   */
  const [createdPersonId, setCreatedPersonId] = useState<number | null>(null);

  /**
   * The vacancy catalogue, asked for only once the dialog is open.
   *
   * ⚠️ `GET /api/v1/roles/` answers an **Interviewer 403**, and TASK-063 asserts this screen makes no
   * request to it for them. They never see **Add candidate** (§5, `candidateAccess.ts`) so they can
   * never open this, and `enabled` keeps it that way for a closed dialog too. §6.3's vacancy filter
   * has already filled this cache entry for an Admin or a Recruiter, so opening the dialog usually
   * costs no request at all.
   */
  const catalogue = useQuery({ ...rolesQuery(), enabled: open });

  /** The three keys ADR 0014 matches on, trimmed as the schema will trim them. */
  const keys: DuplicateCheck = {
    full_name: values.full_name.trim(),
    email: values.email.trim(),
    phone: values.phone.trim(),
  };
  const settledKeys = useSettledKeys(keys, DUPLICATE_CHECK_DEBOUNCE_MS);
  const hasMatchKey =
    settledKeys.full_name !== '' || settledKeys.email !== '' || settledKeys.phone !== '';

  /**
   * The pre-check: *"the form says so **before** saving"*.
   *
   * ⚠️ **Not gated on all three fields being filled.** A blank key is left out of the query entirely
   * (ADR 0014), so a form holding only a name is a legitimate check and the most common one there is.
   * It is a query rather than a mutation because it changes nothing and because a key resolves one
   * answer: two checks in flight cannot land out of order and warn about a name that has since been
   * corrected.
   */
  const check = useQuery({
    queryKey: queryKeys.duplicateCheck(viewerId, settledKeys),
    queryFn: () => checkDuplicates(settledKeys),
    enabled: open && hasMatchKey && createdPersonId === null,
    /**
     * ⚠️ **`gcTime: 0`, decided at the sprint-end review** (both reviewers, 2026-07-28) — the answer
     * does not outlive the dialog that asked for it.
     *
     * The body is other people's personal data: `PossibleDuplicateOut` carries `full_name`, `email`,
     * `phone` and `source` for each match, and the key is the name, email and phone that were typed
     * to find them. With the default `gcTime` that sat in memory for five minutes after the dialog
     * closed, readable from devtools by anyone at the machine, and it was re-served without a
     * request — which is how an erased person came back on screen after ADR 0009 had erased her
     * (`AnonymizeDialog`). Both halves are shut here: the entry is dropped as soon as nothing is
     * looking at it, and a created person cannot leave a stale *"no duplicates"* behind for the next
     * time the same name is typed.
     *
     * What it costs is the reuse the key was designed for — retyping a name inside one dialog after
     * correcting it now asks again. That is one request against a 300/min budget (BUG-011), and the
     * 300 ms debounce already collapses the common case; the cache was never the thing keeping this
     * screen under the throttle.
     */
    gcTime: 0,
  });

  const create = useMutation<
    Person,
    ApiError,
    { person: NewPersonAttempt; acknowledgeDuplicates: boolean }
  >({
    mutationFn: ({ person, acknowledgeDuplicates }) =>
      acknowledgeDuplicates ? createPersonDespiteDuplicates(person) : createPerson(person),
    onSuccess: (person) => setCreatedPersonId(person.id),
  });

  const openRun = useMutation<ConsiderationOpened, ApiError, { personId: number; roleId: number }>({
    mutationFn: ({ personId, roleId }) => openConsideration(personId, { role_id: roleId }),
  });

  const isBusy = create.isPending || openRun.isPending;

  const closeAndReset = () => {
    form.reset();
    create.reset();
    openRun.reset();
    setCreatedPersonId(null);
    onOpenChange(false);
  };

  /**
   * A write landed. The list is invalidated by **prefix**, because a new row belongs to every
   * viewer's every filter (`queryKeyRoots`), which is what puts it on screen without a reload; the
   * person family goes with it, since a new run changes the card this may navigate to.
   *
   * Not awaited: the dialog closes on the answer the server already gave, not on the refetch.
   */
  const finish = (personId: number, goToCard: boolean) => {
    void queryClient.invalidateQueries({ queryKey: queryKeyRoots.considerations });
    void queryClient.invalidateQueries({ queryKey: queryKeyRoots.person });
    closeAndReset();
    if (goToCard) void navigate(`/candidates/${personId}`);
  };

  /**
   * Create the person, then open the first run — **two requests, and the second is not a retry of the
   * first.** `createdPersonId` is what makes the retry idempotent: once `POST /people/` has answered,
   * a second press opens the run on the person that exists rather than creating another one.
   */
  const attempt = async (submitted: CandidateForm, acknowledgeDuplicates: boolean) => {
    try {
      const personId =
        createdPersonId ??
        (
          await create.mutateAsync({
            // Exactly four of `PersonIn`'s five fields, and **no tenant**: the backend resolves it
            // from the authenticated user (`.claude/rules/tenancy.md`). The fifth field is the
            // override, and it belongs to `createPersonDespiteDuplicates` alone.
            person: {
              full_name: submitted.full_name,
              email: submitted.email,
              phone: submitted.phone,
              source: submitted.source,
            },
            acknowledgeDuplicates,
          })
        ).id;

      await openRun.mutateAsync({ personId, roleId: Number(submitted.vacancy) });
      finish(personId, false);
    } catch {
      // Both failures are already held by the mutation that produced them and rendered below;
      // rethrowing here would only reach the console as an unhandled rejection.
    }
  };

  const submit = form.handleSubmit((submitted) => attempt(submitted, false));

  /** §6.4's secondary action — the one call that carries ADR 0014's explicit acknowledgement. */
  const createSeparate = form.handleSubmit((submitted) => attempt(submitted, true));

  /**
   * §6.4's **primary** action: a second consideration on the card that already exists (ADR 0002).
   *
   * Only the vacancy is validated, deliberately: the name in the form is not being saved anywhere on
   * this path, and a recruiter who typed only an email — which is what matched — must not be told to
   * fill in a name before they are allowed to reach the card that already has one.
   */
  const addToExistingCard = async (personId: number) => {
    if (!(await form.trigger('vacancy'))) return;

    try {
      await openRun.mutateAsync({ personId, roleId: Number(form.getValues('vacancy')) });
      finish(personId, true);
    } catch {
      // As above: rendered from `openRun.error`, which is where the 409's own body lives.
    }
  };

  /**
   * Whether the answer `create` holds is still about what is in the form.
   *
   * A 409 is an answer about the body that was sent. Once any of the three match keys changes, the
   * warning it carries describes a body nobody would send again — and leaving it up would go on
   * offering *"add a run to Ivan Petrov's card"* after the name had been corrected. `source` is
   * deliberately not compared: the matcher never looks at it.
   */
  const attempted = create.variables?.person;
  const answerIsCurrent =
    attempted !== undefined &&
    attempted.full_name === keys.full_name &&
    attempted.email === keys.email &&
    attempted.phone === keys.phone;

  const createFailure = answerIsCurrent ? create.error : null;
  /** ⚠️ The 409 of `POST /people/`, read as the interaction it is (ADR 0014) rather than as a fault. */
  const warned = createFailure?.bodyWith('possible_duplicates')?.possible_duplicates;

  /**
   * One list, whichever path found it: the pre-check's answer, or the 409's — the same
   * `PossibleDuplicateOut` entries either way, which is what lets one component render both.
   */
  const duplicates: PossibleDuplicate[] =
    createdPersonId !== null ? [] : (warned ?? check.data?.possible_duplicates ?? []);

  /**
   * The 409 of `POST /people/{id}/considerations/` that names the run already in the way. The two
   * 409s of that route are told apart **by shape** and not by a null field (`ConsiderationRefused`'s
   * own docstring), so this is the discriminator rather than the status code.
   */
  const existingRun = openRun.error?.bodyWith('existing_consideration')?.existing_consideration;
  const existingRunName =
    existingRun && existingRun.person_full_name.trim() === ''
      ? t('candidates:anonymized')
      : existingRun?.person_full_name;

  /** §8 inside a dialog: what failed, in words. Never a status code, never a stack trace. */
  const createLine = (() => {
    if (!createFailure || warned) return null;
    /**
     * ⚠️ **The write sentence, not §8's read banner (BUG-017).** Nothing queues this `POST`, so
     * *"changes will be sent when the connection returns"* was false here — and worst on this route: a
     * recruiter who believes the candidate is on its way types them in again later, which is the second
     * card ADR 0002 exists to prevent. What is said instead claims nothing about the server, because
     * status 0 means no answer arrived rather than nothing having been created.
     */
    if (createFailure.isOffline) return t('components:state.offlineWrite');
    if (createFailure.isThrottled) return t('components:state.throttled');
    return t('candidates:dialog.saveFailed');
  })();

  const runLine = (() => {
    const failure = openRun.error;
    if (!failure || existingRun) return null;
    // The write sentence here too (BUG-017), and this is §6.4's partial failure when it happens: the
    // person may exist with no run, which is exactly the state a reload is how you find out about.
    if (failure.isOffline) return t('components:state.offlineWrite');
    if (failure.isThrottled) return t('components:state.throttled');
    /**
     * ⚠️ The one 409 of this route with no `existing_consideration` is an **anonymised** person (ADR
     * 0009): their data was erased on request, so nothing may be added to their card. It is reachable
     * here — an Admin can anonymise between the moment a match was shown and the moment the primary
     * action is pressed — and it gets a sentence a recruiter can act on rather than a number.
     */
    if (failure.status === 409) return t('candidates:dialog.refused');
    return t('candidates:dialog.runFailed');
  })();

  const personFieldsLocked = createdPersonId !== null;
  const vacancyOptions = (catalogue.data?.items ?? []).map((role) => ({
    value: String(role.id),
    label: role.title,
  }));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : closeAndReset())}
      title={t('candidates:dialog.title')}
      description={t('candidates:dialog.description')}
      footer={
        <>
          <Button variant="ghost" onClick={closeAndReset}>
            {t('common:cancel')}
          </Button>
          {/*
           * While a warning is on screen the form's own submit is **not** offered: it would send the
           * same body the server has just refused and answer 409 again. §6.4's two actions are the
           * actions in that state, and they live in `DuplicateWarning` where the sentence they answer
           * is.
           */}
          {duplicates.length === 0 && (
            <Button
              /**
               * Not `type="submit"`: this renders in the dialog's actions row, outside the `<form>`
               * below. Enter inside the form still submits, through the same handler.
               */
              onClick={() => void submit()}
              isLoading={isBusy}
            >
              {personFieldsLocked
                ? t('candidates:dialog.partial.retry')
                : t('candidates:dialog.submit')}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          {/* The four fields a person is, shared with §6.5's *Edit* (`PersonFields`). */}
          <FormProvider {...form}>
            <PersonFields disabled={personFieldsLocked} />
          </FormProvider>

          <Field
            label={t('candidates:dialog.vacancy')}
            required
            error={
              form.formState.errors.vacancy ? t('candidates:dialog.vacancyRequired') : undefined
            }
            /**
             * ⚠️ **The two sentences that explain this field are `description`s, not siblings**
             * (BUG-022). Both used to render as plain `<p>`/`<span>` next to the control with no `id`
             * and no `aria-describedby`, and the control they explain is **`disabled`** in the first
             * case — therefore out of the tab order — so a keyboard or screen-reader user moving
             * through the form never reached it and never heard why the last required field was inert.
             * Visually the sentence was right beside it; non-visually it was attached to nothing.
             *
             * The trigger is deliberately **not** enabled to make it focusable (BUG-020's entry
             * forbids that and the reasons hold): a disabled control that is *described* is the goal.
             */
            description={
              catalogue.isSuccess && vacancyOptions.length === 0 ? (
                <EmptyCatalogueNotice groups={groups} />
              ) : catalogue.isError ? (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-negative">{t('candidates:filters.vacancyError')}</span>
                  <Button variant="ghost" size="sm" onClick={() => void catalogue.refetch()}>
                    {t('common:retry')}
                  </Button>
                </span>
              ) : undefined
            }
          >
            {(fieldProps) => (
              <>
                <Select
                  id={fieldProps.id}
                  // ⚠️ **Spread, not picked** (BUG-022): `Field` hands down `aria-describedby` for the
                  // two sentences below the control, and picking only `id` off it — which this call site
                  // did — is how they reached the accessibility tree of nothing.
                  aria-describedby={fieldProps['aria-describedby']}
                  aria-invalid={fieldProps['aria-invalid']}
                  // As in `VacancyFilter`: the visible label carries the same words, and the
                  // `aria-label` is what names a Radix trigger for certain.
                  aria-label={t('candidates:dialog.vacancy')}
                  /**
                   * ⚠️ **The form's own value, empty string and all — which is what keeps this control
                   * controlled from its first render (BUG-021).** It read
                   * `values.vacancy === '' ? undefined : values.vacancy`, and Radix decides
                   * controlled-ness by `prop !== undefined`
                   * (`@radix-ui/react-use-controllable-state`), so the first choice flipped it and
                   * logged *"Select is changing from uncontrolled to controlled"* — measured in a
                   * browser as 0 on load, 0 on opening the dialog, **1** after choosing.
                   *
                   * The empty string is not a sentinel this form has to strip: it is **Radix's own
                   * value for *nothing chosen***, since `shouldShowPlaceholder(value)` in
                   * `@radix-ui/react-select` is `value === '' || value === undefined` — which is also
                   * why `SelectItem` refuses `value=""`. So *"Choose a vacancy"* still shows, `''`
                   * names no option, `zod` still refuses it on submit (`vacancy: z.string().min(1)`),
                   * and `Number(submitted.vacancy)` is never reached with one. Identical to §6.9's fix
                   * (`QuestionBank/QuestionDialog.tsx`, `NOTHING_CHOSEN`), whose docblock carries the
                   * same reasoning for the two Selects it found it on.
                   */
                  value={values.vacancy}
                  onValueChange={(next) => form.setValue('vacancy', next, { shouldValidate: true })}
                  placeholder={
                    catalogue.isPending
                      ? t('candidates:dialog.vacancyLoading')
                      : t('candidates:dialog.vacancyPlaceholder')
                  }
                  options={vacancyOptions}
                  disabled={vacancyOptions.length === 0}
                />
                {/*
                 * ⚠️ **The third and fourth states of this field are in `Field`'s `description` slot**
                 * above (BUG-022) rather than here as siblings, so both are in the accessibility tree
                 * and reach a reader who cannot see them beside the control. The conditions are
                 * unchanged and each still matters: `catalogue.isSuccess` and not the length alone —
                 * in flight the list is empty too and *"Loading vacancies…"* is the truth (BUG-020's
                 * fix) — and a failed catalogue is said out loud with a **Retry** rather than
                 * swallowed, because it also means this required field cannot be filled.
                 */}
              </>
            )}
          </Field>
        </form>

        {/*
         * ⚠️ **The person exists and the run does not** — §6.4's designed partial failure. The person
         * fields above are locked because they have been written; the vacancy stays editable, and the
         * footer's action opens the run on the person that already exists rather than creating a
         * second one. The sentence says what a recruiter cannot otherwise know: a candidate with no
         * run appears in no row of §6.3, so this is not a state to close and forget.
         */}
        {personFieldsLocked && (
          <p className="text-sm text-ink-900">
            {t('candidates:dialog.partial.sentence', { name: values.full_name })}
          </p>
        )}

        {createLine && (
          <p role="alert" className="text-sm text-negative">
            {createLine}
          </p>
        )}

        {runLine && (
          <p role="alert" className="text-sm text-negative">
            {runLine}
          </p>
        )}

        {existingRun && (
          <div
            role="alert"
            className="flex flex-col items-start gap-2 rounded border border-rule-control bg-paper-sunken p-4"
          >
            <p className="text-sm text-ink-900">
              {t('candidates:dialog.existingRun.sentence', {
                name: existingRunName,
                vacancy: existingRun.role_title,
              })}
            </p>
            {/* The row itself has no page (ADR 0002), so the link goes to the card that shows it. */}
            <Link
              to={`/candidates/${existingRun.person_id}`}
              className="rounded text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t('candidates:dialog.existingRun.link')}
            </Link>
          </div>
        )}

        {/*
         * Below the fields on purpose: the warning arrives **while the recruiter is typing**, and
         * above them it would push the field under the cursor down the dialog every time the
         * pre-check settles (§8: never a layout that jumps).
         */}
        {duplicates.length > 0 && (
          <DuplicateWarning
            duplicates={duplicates}
            onAddConsideration={(personId) => void addToExistingCard(personId)}
            onCreateSeparate={() => void createSeparate()}
            isBusy={isBusy}
          />
        )}

        {check.isError && <p className={type.metadata}>{t('candidates:dialog.checkFailed')}</p>}
      </div>
    </Dialog>
  );
}

/**
 * ⚠️ **BUG-020 — the sentence beside a vacancy field that cannot be filled**, because the tenant has no
 * vacancy at all.
 *
 * The path is a designed one and not a corner: §6.2's setup path offers *Add a candidate* to a
 * **Recruiter** whose tenant has no vacancy — `setupSteps.ts` gates the *vacancy* step to `['Admin']`
 * on purpose — so a Recruiter can press the dashboard's own primary action, fill in all four person
 * fields, and find the last required field inert with nothing on screen saying why. The field is
 * `required` (§6.4's table: a person with no consideration appears in **no row** of §6.3), so an empty
 * catalogue makes the whole dialog unusable.
 *
 * ⚠️ **Information, not a fault.** The tenant having no vacancy yet is not this reader's mistake, so
 * this is the shape §6.9's truncation notice uses — a `paper-sunken` panel, `ink-700`, with **no
 * `role="alert"`** and none of §3's negative colour. `ui-reviewer` confirmed that shape *"reads as
 * information, not a fault"* on the live pass that shipped it; a red line here would report the tenant's
 * emptiness as a failure of the form.
 *
 * ⚠️ **The words differ by group, and so does whether they lead anywhere** — §8's *"one action"*, under
 * §5's hidden-never-disabled rule. `post ROLES` is `(401, 403, 403, 201, 403, 403)`
 * (`backend/tests/test_permission_matrix.py` line 192, the row `mayWriteVacancies` is named for), so a
 * **Recruiter cannot create the vacancy that would unblock them**: they are told that one has to exist
 * and that an admin adds it, and they are offered nothing — a link to §6.8 would lead to a catalogue
 * that is equally empty and whose *New vacancy* is not rendered for them either. An **Admin** gets the
 * way there, which is the whole reason this can lead anywhere at all now: `/vacancies` exists
 * (`VACANCIES_PATH`, §6.8).
 *
 * ⚠️ **The field is neither hidden nor enabled beside this.** A required field that vanishes leaves a
 * form that submits and then 422s (TASK-064's argument beside the error line), and one that is enabled
 * opens a list with nothing in it.
 */
function EmptyCatalogueNotice({ groups }: { groups: AccessGroup[] }) {
  const { t } = useTranslation('candidates');
  const mayCreate = mayWriteVacancies(groups);

  return (
    <p className="mt-1 rounded border border-rule-control bg-paper-sunken p-3 text-sm text-ink-700">
      <span className="font-medium text-ink-900">{t('dialog.noVacancies.heading')}</span>{' '}
      {t(mayCreate ? 'dialog.noVacancies.admin' : 'dialog.noVacancies.recruiter')}{' '}
      {mayCreate && (
        <Link
          to={VACANCIES_PATH}
          className="rounded text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {t('dialog.noVacancies.link')}
        </Link>
      )}
    </p>
  );
}
